import React, { useState, useMemo, useEffect, useRef } from "react";
import { Plus } from "lucide-react";
import { PortalDsButton } from "src/components/portal-design-system/portal-ds-button";
import { PortalDsField, PortalDsSelect } from "src/components/portal-design-system/portal-ds-field";
import ActivationTeamTable from "../components/activation-team-table";
import ActivationCommissionWarningsBanner from "../components/activation-commission-warnings-banner";
import PayoutUserDrawer from "../components/payout-user-drawer";
import type { PayoutUser } from "../types/commissions.types";
import {
  useGetCommissionUsersQuery,
  useGetCommissionReportsQuery,
  useGetActivationCommissionWarningsQuery,
  useCreateCommissionUserMutation,
  useUpdateCommissionUserMutation,
} from "../endpoints/commissions.endpoints";
import {
  monthYearToQuarterPeriod,
  useReportCommissionViewPeriod,
} from "../commission-view-period-context";

const PERIOD_TYPE_OPTIONS = [
  { value: "month", label: "Month" },
  { value: "quarter", label: "Quarter" },
];

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => {
  const month = i + 1;
  const label = new Date(2000, i, 1).toLocaleString("default", { month: "long" });
  return { value: String(month), label };
});

function getQuartersForPeriod(): { label: string; year: number; quarter: number }[] {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const currentQuarter = Math.ceil(currentMonth / 3);
  const quarters: { label: string; year: number; quarter: number }[] = [];
  for (let i = -2; i <= 1; i++) {
    const total = currentYear * 4 + currentQuarter + i;
    const year = Math.floor((total - 1) / 4);
    const quarter = ((total - 1) % 4) + 1;
    quarters.push({ label: `Q${quarter} ${year}`, year, quarter });
  }
  return quarters;
}

function getYearsForPeriod(): number[] {
  const y = new Date().getFullYear();
  return [y - 1, y, y + 1];
}

const QUARTERS = getQuartersForPeriod();
const YEARS = getYearsForPeriod();

const ActivationTeamView: React.FC = () => {
  const { data: usersData = [] } = useGetCommissionUsersQuery();
  const [createUser] = useCreateCommissionUserMutation();
  const [updateUser] = useUpdateCommissionUserMutation();
  const [users, setUsers] = useState<PayoutUser[]>([]);
  const prevUsersDataRef = useRef(usersData);

  useEffect(() => {
    if (prevUsersDataRef.current !== usersData) {
      prevUsersDataRef.current = usersData;
      setUsers(usersData);
    } else if (usersData.length > 0 && users.length === 0) {
      setUsers(usersData);
    }
  }, [usersData, users.length]);

  const [periodType, setPeriodType] = useState<"month" | "quarter">("quarter");
  const [selectedMonth, setSelectedMonth] = useState(() => String(new Date().getMonth() + 1));
  const [selectedYear, setSelectedYear] = useState(() => String(new Date().getFullYear()));
  const [selectedQuarter, setSelectedQuarter] = useState(() => {
    const now = new Date();
    const y = now.getFullYear();
    const q = Math.ceil((now.getMonth() + 1) / 3);
    return `Q${q} ${y}`;
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<PayoutUser | null>(null);
  const [isNewUser, setIsNewUser] = useState(false);

  const reportParamsList = useMemo(() => {
    if (periodType === "month") {
      const month = Number(selectedMonth);
      const year = Number(selectedYear);
      const p = { type: "user" as const, month, year };
      return [p];
    }
    const q = QUARTERS.find((x) => x.label === selectedQuarter);
    if (!q) return [];
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    const quarterMonths = [
      { type: "user" as const, month: (q.quarter - 1) * 3 + 1, year: q.year },
      { type: "user" as const, month: (q.quarter - 1) * 3 + 2, year: q.year },
      { type: "user" as const, month: (q.quarter - 1) * 3 + 3, year: q.year },
    ];
    const onlyCompletedMonths = quarterMonths.filter(
      (p) => p.year < currentYear || (p.year === currentYear && p.month < currentMonth)
    );
    if (onlyCompletedMonths.length === 0) return [];
    return onlyCompletedMonths;
  }, [periodType, selectedMonth, selectedYear, selectedQuarter]);

  const reportParams0 = reportParamsList[0];
  const reportParams1 = reportParamsList[1];
  const reportParams2 = reportParamsList[2];
  const skipReports = reportParamsList.length === 0;
  const { data: reports1 = [] } = useGetCommissionReportsQuery(reportParams0 ?? { type: "user" }, { skip: skipReports });
  const { data: reports2 = [] } = useGetCommissionReportsQuery(reportParams1 ?? reportParams0 ?? { type: "user" }, { skip: skipReports || reportParamsList.length < 2 });
  const { data: reports3 = [] } = useGetCommissionReportsQuery(reportParams2 ?? reportParams0 ?? { type: "user" }, { skip: skipReports || reportParamsList.length < 3 });

  const userIdToEstimatedCommission = useMemo(() => {
    const sources = [reports1, reports2, reports3].slice(0, reportParamsList.length);
    const all = sources.flat();
    const byUser: Record<string, number> = {};
    for (const r of all) {
      const id = r.userId ?? r.user ?? "";
      if (!id) continue;
      byUser[id] = (byUser[id] ?? 0) + (r.commission ?? 0);
    }
    return byUser;
  }, [reports1, reports2, reports3, reportParamsList.length]);

  const periodLabel = useMemo(() => {
    if (periodType === "month") {
      const monthIndex = Number(selectedMonth) - 1;
      const monthName = new Date(2000, monthIndex, 1).toLocaleString("default", {
        month: "long",
      });
      return `${monthName} ${selectedYear}`;
    }
    return selectedQuarter;
  }, [periodType, selectedMonth, selectedYear, selectedQuarter]);

  const warningPeriod = useMemo(() => {
    if (periodType === "quarter") {
      const q = QUARTERS.find((x) => x.label === selectedQuarter);
      if (!q) return null;
      return { year: q.year, quarter: q.quarter };
    }
    const month = Number(selectedMonth);
    const year = Number(selectedYear);
    if (!month || !year) return null;
    return { year, quarter: Math.ceil(month / 3) };
  }, [periodType, selectedMonth, selectedYear, selectedQuarter]);

  const viewPeriodForLoad = useMemo(() => {
    if (periodType === "quarter") {
      const q = QUARTERS.find((x) => x.label === selectedQuarter);
      if (!q) return null;
      return { quarter: `Q${q.quarter}`, year: q.year };
    }
    const month = Number(selectedMonth);
    const year = Number(selectedYear);
    if (!month || !year) return null;
    return monthYearToQuarterPeriod(month, year);
  }, [periodType, selectedMonth, selectedYear, selectedQuarter]);

  useReportCommissionViewPeriod(viewPeriodForLoad);

  const { data: activationWarningsData } = useGetActivationCommissionWarningsQuery(
    warningPeriod ?? { year: 0, quarter: 0 },
    { skip: warningPeriod == null }
  );

  const handleRowClick = (user: PayoutUser) => {
    setSelectedUser(user);
    setIsNewUser(false);
    setDrawerOpen(true);
  };

  const handleAddUser = () => {
    setSelectedUser(null);
    setIsNewUser(true);
    setDrawerOpen(true);
  };

  const handleSaveUser = async (payload: Partial<PayoutUser> & { id?: string }) => {
    if (isNewUser) {
      const name = payload.name ?? "";
      const email = payload.email ?? "";
      const rate = payload.activePercent ?? 0;
      try {
        await createUser({ name, email, rate }).unwrap();
        setDrawerOpen(false);
      } catch {
        // Error could be shown via toast
      }
      return;
    }
    if (payload.id) {
      try {
        await updateUser({
          id: payload.id,
          name: payload.name,
          email: payload.email,
          rate: payload.activePercent,
        }).unwrap();
        setDrawerOpen(false);
      } catch {
        // Error could be shown via toast
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-end gap-3">
          <PortalDsField
            label="View by"
            htmlFor="activation-period-type"
            filter
            className="w-[8.5rem]"
          >
            <PortalDsSelect
              id="activation-period-type"
              value={periodType}
              onChange={(e) => setPeriodType(e.target.value as "month" | "quarter")}
            >
              {PERIOD_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </PortalDsSelect>
          </PortalDsField>
          {periodType === "month" ? (
            <>
              <PortalDsField
                label="Month"
                htmlFor="activation-month"
                filter
                className="w-[9.5rem]"
              >
                <PortalDsSelect
                  id="activation-month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                >
                  {MONTH_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </PortalDsSelect>
              </PortalDsField>
              <PortalDsField
                label="Year"
                htmlFor="activation-year"
                filter
                className="w-[7rem]"
              >
                <PortalDsSelect
                  id="activation-year"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                >
                  {YEARS.map((y) => (
                    <option key={y} value={String(y)}>
                      {y}
                    </option>
                  ))}
                </PortalDsSelect>
              </PortalDsField>
            </>
          ) : (
            <PortalDsField
              label="Quarter"
              htmlFor="activation-quarter"
              filter
              className="w-[8.5rem]"
            >
              <PortalDsSelect
                id="activation-quarter"
                value={selectedQuarter}
                onChange={(e) => setSelectedQuarter(e.target.value)}
              >
                {QUARTERS.map((q) => (
                  <option key={q.label} value={q.label}>
                    {q.label}
                  </option>
                ))}
              </PortalDsSelect>
            </PortalDsField>
          )}
        </div>
        <PortalDsButton type="button" variant="secondary" size="sm" onClick={handleAddUser}>
          <Plus className="h-4 w-4" />
          Add user
        </PortalDsButton>
      </div>
      <ActivationCommissionWarningsBanner
        warnings={activationWarningsData?.warnings ?? []}
      />
      <ActivationTeamTable
        users={users}
        quarterLabel={periodLabel}
        onRowClick={handleRowClick}
        userIdToEstimatedCommission={userIdToEstimatedCommission}
      />
      <PayoutUserDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        user={selectedUser}
        isNew={isNewUser}
        onSave={handleSaveUser}
      />
    </div>
  );
};

export default ActivationTeamView;
